"use client";

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Lock, Mail, ArrowRight, AlertCircle, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

interface LockScreenProps {
    onUnlock: (token: string) => void;
}

export function LockScreen({ onUnlock }: LockScreenProps) {
    const [token, setToken] = useState("");
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const handleUnlock = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setIsLoading(true);

        try {
            // Security: Verify token online to prevent offline brute-force attacks.
            // We call the edge function with the token and a dedicated check_auth flag.
            // - 401 Unauthorized: Token is invalid.
            // - 200 OK: Auth passed and confirmed.

            const { error: fnError } = await supabase.functions.invoke('evaluate-answer', {
                body: { check_auth: true },
                headers: {
                    "x-access-token": token
                }
            });

            if (fnError) {
                // Check if it's an Auth failure (401)
                const errorObj = fnError as { context?: { status?: number }; status?: number };
                const status = errorObj.context?.status || errorObj.status;

                if (status === 401) {
                    setError("Invalid access token. Please check your code.");
                } else {
                    console.error("Verification error:", fnError);
                    setError("Verification failed. Please try again.");
                }
                setIsLoading(false);
                return;
            }

            // Success (200 OK)
            // Security: Pass the token to the parent component to be stored in memory.
            // Do NOT store in sessionStorage/localStorage to prevent XSS extraction.
            onUnlock(token);

        } catch (err) {
            console.error("Error validating token:", err);
            setError("An error occurred during validation.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleRequestAccess = () => {
        window.location.href = "mailto:kevin.rassner@gmail.com?subject=Request Access to Agile Coach Competence Test&body=Hi Kevin,%0D%0A%0D%0AI would like to request an access token for the Agile Coach Competence Test tool.%0D%0A%0D%0ABest regards,";
    };

    return (
        <div className="flex items-center justify-center min-h-[60vh] p-4 bg-background text-foreground">
            <Card className="w-full max-w-md shadow-lg border border-border bg-card text-card-foreground">
                <CardHeader className="space-y-1 text-center">
                    <div className="mx-auto bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                        <Lock className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="text-2xl font-bold">Restricted Access</CardTitle>
                    <CardDescription>
                        This assessment tool is currently in restricted preview mode.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleUnlock} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="token">Access Token</Label>
                            <Input
                                id="token"
                                type="password"
                                placeholder="Enter your access code"
                                value={token}
                                onChange={(e) => {
                                    setToken(e.target.value);
                                    setError("");
                                }}
                                className={error ? "border-red-500 focus-visible:ring-red-500" : ""}
                            />
                            {error && (
                                <div className="flex items-center gap-2 text-sm text-red-500 mt-1 animate-in fade-in slide-in-from-top-1">
                                    <AlertCircle className="w-4 h-4" />
                                    <span>{error}</span>
                                </div>
                            )}
                        </div>
                        <Button type="submit" className="w-full gap-2" disabled={isLoading}>
                            {isLoading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Verifying...
                                </>
                            ) : (
                                <>
                                    Unlock Assessment <ArrowRight className="w-4 h-4" />
                                </>
                            )}
                        </Button>
                    </form>
                </CardContent>
                <CardFooter className="flex flex-col space-y-4 border-t bg-muted/20 p-6 rounded-b-xl">
                    <div className="text-sm text-center text-muted-foreground">
                        Don't have an access code?
                    </div>
                    <Button variant="outline" onClick={handleRequestAccess} className="w-full gap-2 text-muted-foreground hover:text-foreground">
                        <Mail className="w-4 h-4" />
                        Request Access via Email
                    </Button>
                </CardFooter>
            </Card>
        </div>
    );
}
